import { Injectable } from '@angular/core';
import { EsriActionButton, EsriReactiveUtils } from '../../../../config/esri.module';
import { IdentifyPopupComponent } from '../identify-popup/identify-popup.component';
import { ArbeitsbereichService } from '../../../../shared/service/arbeitsbereich.service';
import { TranslateService } from '@ngx-translate/core';
import { MainMapService } from '../../../../shared/service/main-map.service';
import { CoordinatesPopupComponent } from '../coordinates-popup/coordinates-popup.component';
import Collection from '@arcgis/core/core/Collection';
import ActionButton from '@arcgis/core/support/actions/ActionButton';
import Popup from '@arcgis/core/widgets/Popup';
import {SelectionService} from "../../../../shared/service/selection.service";

interface PopupActionBtn {
  title?: string;
  id?: string;
  className?: string;
}

/**
 * @class PopupService
 * @version 0.0.1
 * @description The PopupService provides the popup component
 */
@Injectable({
  providedIn: 'root',
})
export class PopupService {

  constructor(
    private mainMapService: MainMapService,
    private translateService: TranslateService,
    private arbeitsbereichService: ArbeitsbereichService,
    private selectionService: SelectionService
  ) {
    EsriReactiveUtils.watch(
      () => this.mainMapService.mapView.popup.visible,
      (isPopUpVisible: boolean) => {
        this.handleMapInteractions(isPopUpVisible);
      }
    );
  }

  /**
   * @param response object state of response-idenitify, event click, arbeitsbereich config
   * @param evt
   * @description build popup for identification features list
   */
  public buildIdentifyPopup(response: any, evt: any) {
    this.clearPopup();
    this.mainMapService.mapView.popup.when(() => {
      this.selectionService.removeAllSelectedGraphics();
      const popupComponent = this.createIdentifyPopupComponent();
      popupComponent.init(evt, response, this.arbeitsbereichService.selectedArbeitsbereich.popupTitle);
      if (this.mainMapService.mapView.popup.viewModel) {
        this.mainMapService.mapView.popup.viewModel.actions.removeAll();
      }
    });
  }

  /**
   * @param evt
   * @description build popup for show Coordinates by right-click on the map
   */
  public buildCoordinatesPopup(evt: any) {
    this.clearPopup();

    this.mainMapService.mapView.popup.when(() => {
      const popupComponent = this.createCoordinatesPopupComponent();
      popupComponent.init(evt);

      let actions: Array<PopupActionBtn>;
      this.translateService.get('POPUP.ACTIONS.COPY_ACTION').subscribe(label => {
        actions = [
          {
            title: label,
            id: 'copy-coordinates',
            className: 'esri-icon-duplicate',
          },
        ];
      });

      this.createPopUpActionBtns(actions);
      this.popupCoordinatesTriggers(popupComponent);
    });
  }

  /* Private functions
 ======================== */

  private clearPopup() {
    const mapView = this.mainMapService.mapView;
    mapView.closePopup();
    mapView.popup = new Popup({ view: mapView });
    this.mainMapService.mapView.popup.id = 'popup';
  }

  private handleMapInteractions(isPopUpVisible: boolean) {
    if (!isPopUpVisible) this.mainMapService.activateMapInteraction();
  }

  /**
   * @description trigger actions on popup
   * @param popupComponent
   */
  private popupCoordinatesTriggers(popupComponent: CoordinatesPopupComponent) {
    // This event fires for each click on copy action
    EsriReactiveUtils.on(
      () => this.mainMapService.mapView.popup?.viewModel,
      'trigger-action',
      event => {
        if (event.action.id === 'copy-coordinates') {
          popupComponent.copyCoordinatesToClipboard();
        }
      }
    );
  }

  /**
   * @description Erstellt ein arry von popup action Buttons
   */
  private createPopUpActionBtns(actionBtns: Array<PopupActionBtn>) {
    const popupActions: Array<__esri.ActionButton> = [];
    actionBtns.forEach(actionBtn => {
      const popupActionBtn = this.createActionBtn(actionBtn);
      popupActions.push(popupActionBtn);
    });

    // TODO: if you start app and right click map (coordinate popup) => no viewModel and no container in popup => the popup is not shown ...
    const viewModel = this.mainMapService.mapView.popup.viewModel;
    if (viewModel) {
      if (!viewModel.actions) {
        viewModel.actions = new Collection<ActionButton>();
      } else {
        viewModel.actions.removeAll();
      }
      viewModel.actions.addMany(popupActions);
    }
  }

  /**
   * @returns {popUpActionBtn: __esri.ActionButton}
   * @description Erstellt ein action Button für die Popup
   */
  private createActionBtn(actionBtn: PopupActionBtn): __esri.ActionButton {
    return new EsriActionButton({
      title: actionBtn.title,
      id: actionBtn.id,
      className: actionBtn.className,
    });
  }

  /**
   * @returns {CoordinatesPopupComponent}
   * @description Erstellt ein Koordinaten Popup wenn auf der Karte mit der
   * rechte Maustaste geklickt wird. Das Koordinaten Popup gibt die X und Y Koodrinaten
   * von dem geklickten Punkt der Karte zurück.
   */
  private createCoordinatesPopupComponent(): CoordinatesPopupComponent {
    const componentRef = this.mainMapService.rootViewContainer.createComponent(CoordinatesPopupComponent);
    const coordinatesPopupComponent = componentRef.instance;
    coordinatesPopupComponent.linkComponentRef(componentRef);
    return coordinatesPopupComponent;
  }

  /**
   * @function createIdentifyPopupComponent
   * @returns {IdentifyPopupComponent}
   * @description Erstellt ein Identify Popup wenn auf der Man-Map
   * geklickt wird. Das Identify Popup listet eine Durchstich von alle Features
   * im geklickten Bereich.
   */
  private createIdentifyPopupComponent(): IdentifyPopupComponent {
    const componentRef = this.mainMapService.rootViewContainer.createComponent(IdentifyPopupComponent);
    const identifyPopupComponent = componentRef.instance;
    identifyPopupComponent.linkComponentRef(componentRef);
    return identifyPopupComponent;
  }
}
